import React from "react";
import { X, UserCheck, Eye, History, Tag } from "lucide-react";
import { User } from "./UserDetails";

interface UserItemProps {
  user: User;
  isSelected: boolean;
  onSelect: (userId: string, checked: boolean) => void;
  onAction: (action: string, user: User) => void;
}

// Utility Components
const UserAvatar: React.FC<{ name: string; avatar?: string }> = ({ name, avatar }) => {
  // Protection contre les valeurs nulles ou undefined
  const safeName = name || "Utilisateur Inconnu";
  const initials = safeName
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2); // Limiter à 2 caractères maximum

  if (avatar) {
    return (
      <img
        src={avatar}
        alt={safeName}
        className="w-10 h-10 rounded-full object-cover"
        onError={(e) => {
          // Fallback si l'image ne charge pas
          const target = e.target as HTMLImageElement;
          target.style.display = "none";
        }}
      />
    );
  }

  return (
    <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
      <span className="text-green-700 font-medium text-sm">{initials}</span>
    </div>
  );
};

const StatusBadge: React.FC<{ status: User["kycStatus"] }> = ({ status }) => {
  const statusConfig = {
    pending: { bg: "bg-yellow-100", text: "text-yellow-700", label: "En attente" },
    approved: { bg: "bg-green-100", text: "text-green-700", label: "Approuvé" },
    rejected: { bg: "bg-red-100", text: "text-red-700", label: "Rejeté" },
    incomplete: { bg: "bg-orange-100", text: "text-orange-700", label: "Incomplet" },
    not_requested: { bg: "bg-gray-100", text: "text-gray-700", label: "Non demandé" },
  };

  const config = statusConfig[status] || statusConfig.not_requested;

  return (
    <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>{config.label}</span>
  );
};

const RiskBadge: React.FC<{ level: User["riskLevel"] }> = ({ level }) => {
  const riskConfig = {
    low: { bg: "bg-green-100", text: "text-green-700", label: "Faible" },
    medium: { bg: "bg-yellow-100", text: "text-yellow-700", label: "Moyen" },
    high: { bg: "bg-red-100", text: "text-red-700", label: "Élevé" },
  };

  const config = riskConfig[level] || riskConfig.low;

  return (
    <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>{config.label}</span>
  );
};

// Fonction utilitaire pour formater la date
const formatDate = (dateString: string): string => {
  if (!dateString) return "Date inconnue";

  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "Date invalide";

    return date.toLocaleDateString("fr-FR", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch (error) {
    return "Date invalide";
  }
};

// Fonction utilitaire pour valider un utilisateur
const isValidUser = (user: User): boolean => {
  return !!(user && user.id && user.name && user.email);
};

// UserItem Component
export const UserItem: React.FC<UserItemProps> = ({ user, isSelected, onSelect, onAction }) => {
  // Validation de l'utilisateur

  if (!isValidUser(user)) {
    console.warn("UserItem: Utilisateur invalide", user);
    return (
      <tr className="bg-red-50">
        <td colSpan={7} className="px-6 py-4 text-center text-red-600 text-sm">
          Données utilisateur invalides
        </td>
      </tr>
    );
  }

  const handleSelect = (checked: boolean) => {
    if (user?.id) {
      onSelect(user.id, checked);
    }
  };

  const handleAction = (action: string) => {
    if (user) {
      onAction(action, user);
    }
  };

  return (
    <tr className="hover:bg-gray-50 border-b border-gray-200">
      <td className="px-6 py-4">
        <input
          type="checkbox"
          checked={isSelected}
          onChange={(e) => handleSelect(e.target.checked)}
          className="rounded border-gray-300 text-green-600 focus:ring-green-500 focus:ring-2 focus:ring-offset-1"
        />
      </td>
      <td className="px-6 py-4">
        <div className="flex items-center space-x-3">
          <UserAvatar name={user.name} avatar={user.avatar} />
          <div className="min-w-0 flex-1">
            <p className="font-medium text-gray-800 truncate max-w-[150px]">{user.name || "Nom non renseigné"}</p>
            <p className="text-sm text-gray-600 truncate max-w-[150px]">{user.email || "Email non renseigné"}</p>
          </div>
        </div>
      </td>
      <td className="px-6 py-4">
        <span className="flex items-center space-x-2">
          <span className="text-base">{user.countryFlag || "🌍"}</span>
          <span className="text-sm text-gray-800 truncate max-w-[100px]">{user.country || "Pays inconnu"}</span>
        </span>
      </td>
      <td className="px-6 py-4">
        <StatusBadge status={user.kycStatus} />
      </td>

      <td className="px-6 py-4">
        <ul className="space-y-1 text-sm">
          <li>
            <b>Total envoyé :</b> {user.transaction_stats?.total_sent ?? 0}{" "}
            {user.transaction_stats?.total_sent_currency}
          </li>

          <li>
            <b>Total en CAD :</b> {user.transaction_stats?.total_sent_cad ?? 0} CAD
          </li>

          <li>
            <b>Nombre de transferts :</b> {user.transaction_stats?.transaction_count ?? 0}
          </li>
        </ul>
      </td>
      <td className="px-6 py-4">
        <div className="flex flex-col items-start space-y-2">
          <button
            onClick={() => handleAction("view")}
            className="p-2 text-gray-400 hover:text-green-600 transition-colors rounded-lg hover:bg-green-50"
            title="Voir détails"
          >
            <Eye className="w-4 h-4" />
          </button>

          {(user.kycStatus === "pending" || user.kycStatus === "incomplete") && (
            <>
              <button
                onClick={() => handleAction("approve")}
                className="p-2 text-gray-400 hover:text-green-600 transition-colors rounded-lg hover:bg-green-50"
                title="Approuver"
              >
                <UserCheck className="w-4 h-4" />
              </button>
              <button
                onClick={() => handleAction("reject")}
                className="p-2 text-gray-400 hover:text-red-600 transition-colors rounded-lg hover:bg-red-50"
                title="Rejeter"
              >
                <X className="w-4 h-4" />
              </button>
            </>
          )}

          {/* Nouveau bouton : modifier taux promo */}
          <button
            onClick={() => handleAction("promo-code")}
            className="p-2 text-gray-400 hover:text-blue-600 transition-colors rounded-lg hover:bg-blue-50"
            title="Voir les details  du code promo"
          >
            <Tag className="w-4 h-4" />
          </button>

          {/* Nouveau bouton : historique transactions */}
          <button
            onClick={() => handleAction("transaction-history")}
            className="p-2 text-gray-400 hover:text-purple-600 transition-colors rounded-lg hover:bg-purple-50"
            title="Historique des transactions"
          >
            <History className="w-4 h-4" />
          </button>
        </div>
      </td>
    </tr>
  );
};
